import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import { sections } from './data/journeyQuestions';

// Add these debug logs at the top after imports
console.log('Loading Firebase config...');
console.log('Build environment:', process.env.NODE_ENV);
console.log('Firebase project:', process.env.REACT_APP_FIREBASE_PROJECT_ID);
console.log('Target deployment:', process.env.REACT_APP_DEPLOY_TARGET || 'local');
console.log('Expected auth domain:', process.env.REACT_APP_FIREBASE_AUTH_DOMAIN);

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Debug: Log full config (without sensitive data)
console.log('Firebase Config:', {
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  storageBucket: firebaseConfig.storageBucket
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Debug: Log initialization
console.log('Initializing Firebase functions...');

// Initialize functions with explicit region
const functions = getFunctions(app, 'us-central1');
console.log('Functions initialized:', {
  region: 'us-central1',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID
});

// Create the callable function
export const testNotificationDev = httpsCallable(functions, 'testNotificationDev');

// Initialize auth with LOCAL persistence (keeps user logged in until explicit logout)
// This persists auth state in localStorage, so users stay logged in across browser sessions
// ID tokens expire after 1 hour but are automatically refreshed
// Refresh tokens can last up to 30+ days
const auth = getAuth(app);

// Set persistence to LOCAL (explicit - this is the default but making it clear)
// This keeps users logged in until they explicitly log out or the refresh token expires
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting auth persistence:', error);
});

// Token refresh interval (stored outside to prevent multiple intervals)
let tokenRefreshInterval = null;

// Add auth state listener for debugging and automatic token refresh
auth.onAuthStateChanged((user) => {
  console.log('Auth state changed:', {
    isAuthenticated: !!user,
    uid: user?.uid,
    isAnonymous: user?.isAnonymous,
    tokenPresent: !!user?.getIdToken()
  });
  
  // Clear any existing interval
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
  
  // Automatically refresh token to keep session alive
  if (user) {
    // Refresh token every 50 minutes (tokens expire after 1 hour)
    // This ensures the session stays active without user intervention
    tokenRefreshInterval = setInterval(async () => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          await currentUser.getIdToken(true); // Force refresh
          console.log('Auth token refreshed automatically');
        } else {
          // User logged out, clear interval
          clearInterval(tokenRefreshInterval);
          tokenRefreshInterval = null;
        }
      } catch (error) {
        console.error('Error refreshing auth token:', error);
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }
    }, 50 * 60 * 1000); // 50 minutes
  }
});

// Initialize Firebase messaging
const initializeMessaging = () => {
  try {
    const messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error('Error initializing messaging:', error);
    return null;
  }
};

// Initialize services
const db = getFirestore(app);
const storage = getStorage(app);
const analytics = getAnalytics(app);
const messaging = initializeMessaging();

// Debug: Log initialization
console.log('Firebase initialized with app:', app.name);
console.log('Firestore initialized');

// Function to request notification permission
const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

// Function to request messaging token
export const requestMessagingToken = async () => {
  try {
    const messaging = getMessaging();
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    // Add Safari-specific options
    const tokenOptions = {
      vapidKey: process.env.REACT_APP_VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.ready,
      // Add Safari support
      webkit: {
        applicationServerKey: process.env.REACT_APP_VAPID_KEY,
        pushManager: true
      }
    };

    const token = await getToken(messaging, tokenOptions);
    return token;
  } catch (error) {
    console.error('Error getting token:', error);
    return null;
  }
};

// Get messaging instance
const getMessagingInstance = () => messaging;

// Message listener
const onMessageListener = () =>
  new Promise((resolve) => {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      // Add timezone debug logs
      console.log('Client received message at:', new Date().toISOString());
      console.log('Client timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
      
      try {
        if (Notification.permission === 'granted') {
          const notif = new Notification(payload.notification.title, {
            body: payload.notification.body,
            icon: '/logo192.png',
            requireInteraction: true,
            tag: `reminder-notification-${Date.now()}`,
            renotify: true,
            silent: false,
            vibrate: [200, 100, 200],
            data: {
              url: payload.fcmOptions?.link || '/reminders'
            }
          });
          
          // Play a sound to get attention
          const audio = new Audio('/sounds/notification-sound-TOM.wav');
          audio.play().catch(e => console.log('Audio play failed:', e));
          
          notif.onclick = () => {
            window.focus();
            window.location.href = payload.fcmOptions?.link || '/reminders';
          };
        }
      } catch (error) {
        console.error('Error creating notification:', error);
      }
      
      resolve(payload);
    });
  });

// Updated helper functions for spiritual journey operations with individual question fields

export const saveSpiritualJourney = async (userId, journeyData) => {
  const journeyRef = doc(db, 'users', userId, 'spiritualJourney', 'main');
  
  // Create an object with individual fields for each question
  const questionFields = {};
  journeyData.responses.forEach((sectionResponses, sectionIndex) => {
    sectionResponses.forEach((response, questionIndex) => {
      const fieldName = `section${sectionIndex}_question${questionIndex}`;
      questionFields[fieldName] = response;
    });
  });

  await setDoc(journeyRef, {
    ...questionFields,
    progress: {
      completedSections: journeyData.completedSections || [],
      currentSection: journeyData.currentSection || 0,
      currentStep: journeyData.currentStep || 0,
      totalQuestions: journeyData.responses.flat().length,
      answeredQuestions: journeyData.responses.flat().filter(r => r?.trim()).length,
    },
    compiledStory: journeyData.compiledStory,
    lastUpdated: serverTimestamp()
  }, { merge: true });
};

export const getSpiritualJourney = async (userId) => {
  const journeyRef = doc(db, 'users', userId, 'spiritualJourney', 'main');
  const journeySnap = await getDoc(journeyRef);
  
  if (!journeySnap.exists()) return null;

  const data = journeySnap.data();
  
  // Reconstruct the responses array from individual fields
  const responses = sections.map((section, sectionIndex) => 
    section.questions.map((_, questionIndex) => 
      data[`section${sectionIndex}_question${questionIndex}`] || ""
    )
  );

  return {
    responses,
    progress: data.progress || {
      completedSections: [],
      currentSection: 0,
      currentStep: 0,
      totalQuestions: 0,
      answeredQuestions: 0
    },
    compiledStory: data.compiledStory || "",
    lastUpdated: data.lastUpdated
  };
};

// Add these functions to your firebase.js
export const saveJourney = async (userId, journeyData) => {
  // Flatten the responses array into an object structure
  const flattenedResponses = {};
  journeyData.responses.forEach((section, sIndex) => {
    section.forEach((response, qIndex) => {
      flattenedResponses[`${sIndex}_${qIndex}`] = response;
    });
  });

  const journeyRef = doc(db, 'users', userId, 'spiritualJourney', 'main');
  await setDoc(journeyRef, {
    responses: flattenedResponses,
    lastPosition: journeyData.lastPosition,
    lastUpdated: new Date().toISOString()
  });
};

export const getJourney = async (userId) => {
  const journeyRef = doc(db, 'users', userId, 'spiritualJourney', 'main');
  const journeySnap = await getDoc(journeyRef);
  
  if (!journeySnap.exists()) return null;
  
  const data = journeySnap.data();
  
  // Convert flattened responses back to array structure
  const responses = sections.map(section => 
    new Array(section.questions.length).fill("")
  );
  
  Object.entries(data.responses || {}).forEach(([key, value]) => {
    const [sIndex, qIndex] = key.split('_').map(Number);
    responses[sIndex][qIndex] = value;
  });
  
  return {
    responses,
    lastPosition: data.lastPosition
  };
};

// Export everything
export {
  app,
  db,
  auth,
  storage,
  analytics,
  getMessagingInstance,
  onMessageListener,
  requestNotificationPermission,
  functions
};
